<template>
  <div :class="rootClass" :style="rootStyle">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <h1 v-if="fields?.title" v-html="fields?.title.value" />
      <JssRichText class="locations-desc" :field="fields?.desc" />
      <div class="locations-map">
        <AdaptiveImage class="locations-map-image" :field="fields?.mapImage" :is-background="true">
          <div
            class="locations-map-image-icon"
            :class="{ active: selectedIndex === index }"
            v-for="(icon, index) in fields?.list"
            :key="index"
            :style="{
              left: icon.fields?.iconPositionX?.value + '%',
              top: icon.fields?.iconPositionY?.value + '%',
            }"
            @click="selectIcon(index)"
          >
            <Icon
              :svg="
                selectedIndex === index
                  ? fields.mapActiveIcon?.value?.svgCode
                  : fields.mapIcon?.value?.svgCode
              "
            />
          </div>
        </AdaptiveImage>
        <map-card
          class="locations-map-card-wrapper"
          :class="{ 'card-left': cardPosition === 'left' }"
          :style="cardStyle"
          :item="fields?.list[selectedIndex]"
          :background="fields.cardBackground"
          :positionIcon="fields?.positionIcon"
          :phoneIcon="fields?.phoneIcon"
          :messageIcon="fields?.messageIcon"
          v-show="isCardVisible"
          @close="closeCard"
        />
      </div>
      <div
        ref="mobileSwiperEl"
        class="swiper locations-map-card-mobile-swiper"
        v-show="isCardVisible"
      >
        <div class="swiper-wrapper">
          <div
            class="swiper-slide locations-map-card-mobile-slide"
            v-for="(item, index) in fields?.list"
            :key="index"
          >
            <map-card
              class="locations-map-card-mobile"
              :item="item"
              :background="fields.cardBackground"
              :positionIcon="fields?.positionIcon"
              :phoneIcon="fields?.phoneIcon"
              :messageIcon="fields?.messageIcon"
              @close="closeCard"
            />
          </div>
        </div>
      </div>
    </ContentGrid>
  </div>
</template>

<script>
import { reactive, computed, ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import Swiper from 'swiper';
import { destroySwiper } from '@/utils/site-utils';
import 'swiper/css';
import utils from '@/utils';
import mapCard from './mapCard.vue';

export default {
  name: 'Locations',
  components: {
    mapCard,
  },
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const state = reactive({});
    const selectedIndex = ref(0);
    const isCardVisible = ref(true);
    const mobileSwiperEl = ref(null);
    let mobileSwiper = null;

    const selectIcon = (index) => {
      selectedIndex.value = index;
      isCardVisible.value = true;
      if (mobileSwiper) {
        mobileSwiper.slideTo(index);
      }
    };

    const closeCard = () => {
      isCardVisible.value = false;
    };

    const onSlideChange = (swiper) => {
      selectedIndex.value = swiper.activeIndex;
    };

    const buildSwiper = () => {
      if (!mobileSwiperEl.value) return;
      const listLen = props.fields?.list?.length || 0;
      if (!listLen) return;

      destroySwiper(mobileSwiper, true, true);
      mobileSwiper = new Swiper(mobileSwiperEl.value, {
        slidesPerView: 1,
        spaceBetween: 16,
        watchOverflow: true,
        resizeObserver: true,
        observer: true,
        observeParents: true,
        on: {
          slideChange: onSlideChange,
        },
      });
      if (selectedIndex.value !== mobileSwiper.activeIndex) {
        mobileSwiper.slideTo(selectedIndex.value, 0);
      }
    };

    const cardPosition = computed(() => {
      if (!props.fields?.list || selectedIndex.value === undefined) return 'right';
      const icon = props.fields.list[selectedIndex.value];
      const iconPositionX = parseFloat(icon.fields?.iconPositionX?.value) || 50;
      return iconPositionX > 50 ? 'left' : 'right';
    });

    const cardStyle = computed(() => {
      if (!props.fields?.list || selectedIndex.value === undefined) return {};
      const icon = props.fields.list[selectedIndex.value];
      const iconPositionX = parseFloat(icon.fields?.iconPositionX?.value) || 50;
      const iconPositionY = parseFloat(icon.fields?.iconPositionY?.value) || 50;
      const isLeft = iconPositionX > 50;

      return {
        left: isLeft ? 'auto' : iconPositionX + 20 + '%',
        right: isLeft ? 100 - iconPositionX + 20 + '%' : 'auto',
        top: iconPositionY - 10 + '%',
      };
    });

    const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'locations'));

    const rootStyle = computed(() => {
      return utils.overall.baseComStyle(props.fields);
    });

    watch(
      () => props.fields?.list?.length,
      // eslint-disable-next-line vue/valid-next-tick
      () => nextTick(() => buildSwiper())
    );

    onMounted(() => {
      nextTick(() => buildSwiper());
    });

    onBeforeUnmount(() => {
      destroySwiper(mobileSwiper, true, true);
    });

    return {
      state,
      selectIcon,
      selectedIndex,
      cardPosition,
      cardStyle,
      rootClass,
      rootStyle,
      isCardVisible,
      closeCard,
      mobileSwiperEl,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.locations {
  position: relative;
  width: 100%;
  padding: 40px 0;

  &-title {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.3;
    margin-bottom: 16px;
  }

  &-desc {
    font-size: 14px;
    color: $grey-black;
    line-height: 1.4;
  }

  &-map {
    position: relative;
    width: 100%;
    aspect-ratio: 2 / 1;
    margin-top: 56px;

    &-image {
      position: relative;
      width: 100%;
      height: 100%;

      &-icon {
        width: 22px;
        height: 32px;
        position: absolute;
        left: 0;
        bottom: 0;
        transform: translateX(-50%);
        transform-origin: bottom center;
        z-index: 10;
        cursor: pointer;
        opacity: 0.7;
        transition:
          transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1),
          opacity 0.3s ease;

        &.active {
          z-index: 20;
          opacity: 1;
          width: 33px;
          height: 48px;
          transform: translateX(-50%) translateY(-13px);
          animation: iconScaleTransition 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

          svg {
            width: 100%;
            height: 100%;
          }
        }

        svg {
          width: 100%;
          height: 100%;
          transition:
            width 0.4s cubic-bezier(0.34, 1.56, 0.64, 1),
            height 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      }
    }
  }

  &-map-card-wrapper {
    display: none;
  }

  &-map-card-mobile-swiper {
    display: block;
    margin-top: 16px;
    overflow: hidden;

    .locations-map-card-mobile {
      margin-top: 0;
      width: 100%;
    }
  }

  &-map-card-mobile-slide {
    height: auto;
  }

  @include tablet-landscape {
    padding: 60px 0;

    &-title {
      font-size: 32px;
    }

    &-map {
      width: calc(100% - 200px);
      margin: 50px auto 0;
    }

    &-map-card-wrapper {
      display: block;
      position: absolute;
      width: 320px;
      z-index: 30;
      transition: all 0.3s ease;

      &.card-left {
        transform: translateX(50%);
      }

      &:not(.card-left) {
        transform: translateX(-50%);
      }
    }

    &-map-card-mobile-swiper {
      display: none;
    }
  }
}
</style>
